import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LogIn, AlertTriangle } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, sessionError, setSessionError } = useAuth();
  const navigate = useNavigate();

  // Show session error if user was logged out due to being disabled
  useEffect(() => {
    if (sessionError) {
      setError(sessionError);
      // Clear the session error after displaying it
      setSessionError(null);
    }
  }, [sessionError, setSessionError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cloud-white flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png"
              alt="Fearless IT"
              className="h-10 mx-auto mb-6"
              data-testid="login-logo"
            />
          </Link>
          <h1 className="text-3xl font-bold text-carbon-black" data-testid="login-title">Welcome Back</h1>
          <p className="text-slate-gray mt-2">Sign in to your Fearless IT account</p>
        </div>

        <Card className="shadow-xl rounded-2xl border-0">
          <CardHeader>
            <CardTitle className="text-center">Sign In</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start" data-testid="login-error">
                  <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                  placeholder="you@company.com"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-carbon-black">Password</label>
                  <Link to="/forgot-password" className="text-sm text-fearless-blue hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full gradient-fearless text-white py-6 text-lg font-semibold"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : (
                  <>
                    <LogIn className="mr-2 h-5 w-5" />
                    Sign In
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-gray text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="text-fearless-blue font-semibold hover:underline" data-testid="login-register-link">
                  Sign up
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;